import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectTimelineById,
  addNoteToTimeline,
  updateNoteInTimeline,
  removeNoteFromTimeline,
  addChapterBorder,
  updateChapterBorder,
  removeChapterBorder,
  toggleChapterBorders,
  updateViewState,
  resetViewState,
  updateTimeline,
} from './timelinesSlice';
import timelineService from '../../utils/apiRequests/timelineService';

const TimelineContext = createContext(null);

/**
 * Generate a unique ID without uuid dependency
 */
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Tool modes available in the timeline
 */
export const TOOL_MODES = {
  CURSOR: 'cursor',
  PAN: 'pan',
  PLACE_NOTE: 'place_note',
};

/**
 * Row height for snapping notes vertically
 * Notes are 200px tall, so 220px row height gives 20px spacing
 */
export const ROW_HEIGHT = 220;

/**
 * TimelineProvider manages all state for a single timeline canvas
 */
export const TimelineProvider = ({ timelineId, children }) => {
  const dispatch = useDispatch();
  const timeline = useSelector(selectTimelineById(timelineId));

  // Local state for interactions
  const [toolMode, setToolMode] = useState(TOOL_MODES.CURSOR);
  const [selectedBorderId, setSelectedBorderId] = useState(null);

  // Undo/Redo history
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoRedoAction = useRef(false);

  // Auto-save refs
  const viewStateSaveTimeoutRef = useRef(null);
  const notesBordersSaveTimeoutRef = useRef(null);
  const lastSavedViewStateRef = useRef(null);
  const lastSavedNotesBordersRef = useRef(null);
  const isInitialLoadRef = useRef(true);

  // Initialize history with current state
  useEffect(() => {
    if (timeline && history.length === 0) {
      setHistory([{ notes: timeline.notes, chapterBorders: timeline.chapterBorders }]);
      setHistoryIndex(0);
    }
  }, [timeline, history.length]);

  // Save state to history (for undo/redo)
  const saveToHistory = useCallback(() => {
    if (isUndoRedoAction.current || !timeline) {
      isUndoRedoAction.current = false;
      return;
    }

    const newState = { notes: [...timeline.notes], chapterBorders: [...timeline.chapterBorders] };
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newState);

    // Limit history to 50 entries
    if (newHistory.length > 50) {
      newHistory.shift();
    }

    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [timeline, history, historyIndex]);

  // View state - memoized to prevent unnecessary re-renders
  const viewState = useMemo(
    () => timeline?.viewState || { panX: 0, panY: 0, zoom: 1 },
    [timeline?.viewState]
  );

  const showChapterBorders = useMemo(
    () => timeline?.showChapterBorders ?? true,
    [timeline?.showChapterBorders]
  );

  const setPan = useCallback(
    (panX, panY) => {
      dispatch(updateViewState({ timelineId, viewState: { panX, panY } }));
    },
    [dispatch, timelineId]
  );

  const setZoom = useCallback(
    (zoom) => {
      const clampedZoom = Math.max(0.1, Math.min(5, zoom));
      dispatch(updateViewState({ timelineId, viewState: { zoom: clampedZoom } }));
    },
    [dispatch, timelineId]
  );

  const resetZoomFn = useCallback(() => {
    dispatch(resetViewState({ timelineId }));
  }, [dispatch, timelineId]);

  // Note operations
  const addNote = useCallback(
    (noteData = {}) => {
      const newNote = {
        _id: generateId(),
        title: noteData.title || 'New Note',
        content: noteData.content || '',
        createdAt: Date.now(),
        updatedEpoch: Date.now(),
        deletedTimestamp: null,
        color: noteData.color || null,
        textColor: noteData.textColor || null,
        relatedChapters: [],
        relatedCharacters: [],
        relatedNotes: [],
        type: noteData.type || 'generic',
        tags: noteData.tags || [],
        category: noteData.category || 'General',
        // Timeline-specific fields
        x: noteData.x ?? 100,
        // Snap Y to nearest row
        y: Math.max(0, Math.round((noteData.y ?? 0) / ROW_HEIGHT) * ROW_HEIGHT),
        width: noteData.width ?? 200,
        height: 200, // Fixed height
      };
      dispatch(addNoteToTimeline({ timelineId, note: newNote }));
      saveToHistory();
      return newNote;
    },
    [dispatch, timelineId, saveToHistory]
  );

  const updateNote = useCallback(
    (noteId, updates) => {
      // Prevent height changes
      const safeUpdates = { ...updates };
      if ('height' in safeUpdates) {
        delete safeUpdates.height;
      }
      dispatch(updateNoteInTimeline({ timelineId, noteId, updates: safeUpdates }));
      saveToHistory();
    },
    [dispatch, timelineId, saveToHistory]
  );

  const deleteNote = useCallback(
    (noteId) => {
      dispatch(removeNoteFromTimeline({ timelineId, noteId }));
      saveToHistory();
    },
    [dispatch, timelineId, saveToHistory]
  );

  const moveNote = useCallback(
    (noteId, x, y, snapToRow = true) => {
      // Snap Y position to nearest row
      const snappedY = snapToRow ? Math.round(y / ROW_HEIGHT) * ROW_HEIGHT : y;
      // Ensure Y doesn't go negative (above the ceiling)
      const finalY = Math.max(0, snappedY);
      dispatch(updateNoteInTimeline({ timelineId, noteId, updates: { x, y: finalY } }));
    },
    [dispatch, timelineId]
  );

  const resizeNoteWidth = useCallback(
    (noteId, width, x = null) => {
      const updates = { width: Math.max(150, width) };
      if (x !== null) {
        updates.x = x;
      }
      dispatch(updateNoteInTimeline({ timelineId, noteId, updates }));
    },
    [dispatch, timelineId]
  );

  // Bring a note to the front (highest z-index)
  const bringNoteToFront = useCallback(
    (noteId) => {
      const notesList = timeline?.notes || [];
      const maxZIndex = notesList.reduce((max, n) => Math.max(max, n.zIndex || 0), 0);
      dispatch(updateNoteInTimeline({ timelineId, noteId, updates: { zIndex: maxZIndex + 1 } }));
    },
    [dispatch, timelineId, timeline]
  );

  // Chapter border operations
  const addBorder = useCallback(
    (borderData = {}) => {
      const newBorder = {
        id: generateId(),
        x: borderData.x ?? 500,
        label: borderData.label || '',
        color: borderData.color || 'gray',
      };
      dispatch(addChapterBorder({ timelineId, chapterBorder: newBorder }));
      saveToHistory();
      return newBorder;
    },
    [dispatch, timelineId, saveToHistory]
  );

  const updateBorder = useCallback(
    (borderId, updates) => {
      dispatch(updateChapterBorder({ timelineId, borderId, updates }));
    },
    [dispatch, timelineId]
  );

  const moveBorder = useCallback(
    (borderId, x) => {
      // No snapping - place wherever the user wants
      dispatch(updateChapterBorder({ timelineId, borderId, updates: { x } }));
    },
    [dispatch, timelineId]
  );

  const deleteBorder = useCallback(
    (borderId) => {
      dispatch(removeChapterBorder({ timelineId, borderId }));
      if (selectedBorderId === borderId) {
        setSelectedBorderId(null);
      }
      saveToHistory();
    },
    [dispatch, timelineId, saveToHistory, selectedBorderId]
  );

  const toggleBordersVisibility = useCallback(() => {
    dispatch(toggleChapterBorders({ timelineId }));
  }, [dispatch, timelineId]);

  // Undo/Redo
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      isUndoRedoAction.current = true;
      const prevState = history[historyIndex - 1];
      dispatch(
        updateTimeline({
          id: timelineId,
          notes: prevState.notes,
          chapterBorders: prevState.chapterBorders,
        })
      );
      setHistoryIndex(historyIndex - 1);
    }
  }, [historyIndex, history, dispatch, timelineId]);

  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      isUndoRedoAction.current = true;
      const nextState = history[historyIndex + 1];
      dispatch(
        updateTimeline({
          id: timelineId,
          notes: nextState.notes,
          chapterBorders: nextState.chapterBorders,
        })
      );
      setHistoryIndex(historyIndex + 1);
    }
  }, [historyIndex, history, dispatch, timelineId]);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  const value = useMemo(
    () => ({
      // Timeline data
      timeline,
      timelineId,
      notes: timeline?.notes || [],
      chapterBorders: timeline?.chapterBorders || [],
      showChapterBorders,

      // View state
      viewState,
      setPan,
      setZoom,
      resetZoom: resetZoomFn,

      // Tool mode
      toolMode,
      setToolMode,

      // Note operations
      addNote,
      updateNote,
      deleteNote,
      moveNote,
      resizeNoteWidth,
      bringNoteToFront,

      // Chapter border operations
      addBorder,
      updateBorder,
      moveBorder,
      deleteBorder,
      toggleBordersVisibility,
      selectedBorderId,
      setSelectedBorderId,

      // Undo/Redo
      undo,
      redo,
      canUndo,
      canRedo,
    }),
    [
      timeline,
      timelineId,
      showChapterBorders,
      viewState,
      setPan,
      setZoom,
      resetZoomFn,
      toolMode,
      addNote,
      updateNote,
      deleteNote,
      moveNote,
      resizeNoteWidth,
      bringNoteToFront,
      addBorder,
      updateBorder,
      moveBorder,
      deleteBorder,
      toggleBordersVisibility,
      selectedBorderId,
      undo,
      redo,
      canUndo,
      canRedo,
    ]
  );

  // Auto-save functionality
  useEffect(() => {
    if (!timeline || !timeline.projectId || isInitialLoadRef.current) {
      if (timeline) {
        // Mark initial load as complete
        isInitialLoadRef.current = false;
        lastSavedViewStateRef.current = JSON.stringify(timeline.viewState);
        // Extract note IDs for comparison (notes might be populated objects or IDs)
        const noteIds = (timeline.notes || []).map((note) => {
          if (typeof note === 'string') return note;
          return note._id || note.id || note;
        });
        lastSavedNotesBordersRef.current = JSON.stringify({
          notes: noteIds,
          chapterBorders: timeline.chapterBorders,
          showChapterBorders: timeline.showChapterBorders,
        });
      }
      return () => {
        // No cleanup needed on early return
      };
    }

    // Auto-save viewState with debouncing (1 second delay for pan/zoom)
    const currentViewStateStr = JSON.stringify(timeline.viewState);
    if (currentViewStateStr !== lastSavedViewStateRef.current) {
      // Clear existing timeout
      if (viewStateSaveTimeoutRef.current) {
        clearTimeout(viewStateSaveTimeoutRef.current);
      }

      // Set new timeout
      viewStateSaveTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await timelineService.updateTimeline(timeline.projectId, timelineId, {
            viewState: timeline.viewState,
          });

          if (response.error) {
            console.error('Error auto-saving viewState:', response.error);
          } else {
            lastSavedViewStateRef.current = currentViewStateStr;
          }
        } catch (err) {
          console.error('Error auto-saving viewState:', err);
        }
      }, 1000); // 1 second debounce for viewState
    }

    // Auto-save notes/chapterBorders with debouncing (500ms delay)
    // Extract note IDs (notes might be populated objects or IDs)
    const noteIds = (timeline.notes || []).map((note) => {
      if (typeof note === 'string') return note;
      return note._id || note.id || note;
    });
    const currentNotesBordersStr = JSON.stringify({
      notes: noteIds,
      chapterBorders: timeline.chapterBorders,
      showChapterBorders: timeline.showChapterBorders,
    });
    if (currentNotesBordersStr !== lastSavedNotesBordersRef.current) {
      // Clear existing timeout
      if (notesBordersSaveTimeoutRef.current) {
        clearTimeout(notesBordersSaveTimeoutRef.current);
      }

      // Set new timeout
      notesBordersSaveTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await timelineService.updateTimeline(timeline.projectId, timelineId, {
            notes: noteIds,
            chapterBorders: timeline.chapterBorders,
            showChapterBorders: timeline.showChapterBorders,
          });

          if (response.error) {
            console.error('Error auto-saving notes/chapterBorders:', response.error);
          } else {
            lastSavedNotesBordersRef.current = currentNotesBordersStr;
          }
        } catch (err) {
          console.error('Error auto-saving notes/chapterBorders:', err);
        }
      }, 500); // 500ms debounce for notes/chapterBorders
    }

    // Cleanup function
    return () => {
      if (viewStateSaveTimeoutRef.current) {
        clearTimeout(viewStateSaveTimeoutRef.current);
      }
      if (notesBordersSaveTimeoutRef.current) {
        clearTimeout(notesBordersSaveTimeoutRef.current);
      }
    };
  }, [timeline, timelineId]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (viewStateSaveTimeoutRef.current) {
        clearTimeout(viewStateSaveTimeoutRef.current);
      }
      if (notesBordersSaveTimeoutRef.current) {
        clearTimeout(notesBordersSaveTimeoutRef.current);
      }
    };
  }, []);

  return <TimelineContext.Provider value={value}>{children}</TimelineContext.Provider>;
};

TimelineProvider.propTypes = {
  timelineId: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

/**
 * Hook to access timeline context
 */
export const useTimeline = () => {
  const context = useContext(TimelineContext);
  if (!context) {
    throw new Error('useTimeline must be used within a TimelineProvider');
  }
  return context;
};

export default TimelineProvider;
